import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getUserAnalyticsReport } from '../../actions/dashboard';
import { 
    Row, 
    Col, 
    Card, 
    Button, 
    Alert, 
    Spinner, 
    Table, 
    Badge
} from 'react-bootstrap';
import { 
    BarChart3, 
    Download, 
    RefreshCw,
    Users,
    UserX,
    MapPin,
    ShoppingBag,
    AlertCircle,
    Mail,
    Calendar
} from 'lucide-react';

const UserAnalyticsReport = ({ 
    getUserAnalyticsReport, 
    userAnalyticsReport, 
    reportLoading, 
    reportError 
}) => {
    const [expandedCategory, setExpandedCategory] = useState(null);

    useEffect(() => {
        getUserAnalyticsReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleExportExcel = () => {
        getUserAnalyticsReport(true);
    };

    const handleRefresh = () => {
        getUserAnalyticsReport(false);
    };

    const toggleCategory = (index) => {
        setExpandedCategory(expandedCategory === index ? null : index);
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === 'N/A') return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getCategoryIcon = (category) => {
        const iconMap = {
            'Total Users': <Users size={20} className="text-primary" />,
            'Users Dropped Off Sign Up': <UserX size={20} className="text-warning" />,
            'Users Not in Serviceable Area': <MapPin size={20} className="text-danger" />,
            'Users Without Address': <MapPin size={20} className="text-secondary" />,
            'Users Without Purchase': <ShoppingBag size={20} className="text-info" />,
            'Users Purchased But No Delivery Request': <AlertCircle size={20} className="text-warning" />
        };
        return iconMap[category] || <Users size={20} />;
    };

    const getCategoryVariant = (category) => {
        const variantMap = {
            'Total Users': 'primary',
            'Users Dropped Off Sign Up': 'warning',
            'Users Not in Serviceable Area': 'danger',
            'Users Without Address': 'secondary',
            'Users Without Purchase': 'info',
            'Users Purchased But No Delivery Request': 'warning'
        };
        return variantMap[category] || 'primary';
    };

    return (
        <Fragment>
            <div className="container-fluid py-4" style={{background: '#f8fafc', minHeight: '100vh'}}>
                {/* Header */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <div>
                            <h2 className="text-primary mb-1">
                                <BarChart3 size={32} className="me-3" />
                                User Analytics Report
                            </h2>
                            <p className="text-muted mb-0">
                                Aggregate user statistics and user categorization
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                onClick={handleRefresh}
                                disabled={reportLoading}
                            >
                                <RefreshCw size={16} className="me-2" />
                                Refresh
                            </Button>
                            <Button
                                variant="success"
                                onClick={handleExportExcel}
                                disabled={reportLoading}
                            >
                                <Download size={16} className="me-2" />
                                Export Excel
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Error Alert */}
                {reportError && (
                    <Alert variant="danger" dismissible>
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {reportError}
                    </Alert>
                )}

                {/* Loading State */}
                {reportLoading && (
                    <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Loading user analytics report...</p>
                        </div>
                    </div>
                )}

                {/* Summary Statistics */}
                {!reportLoading && userAnalyticsReport && userAnalyticsReport.data && (
                    <>
                        <Card className="shadow-sm">
                            <Card.Header className="bg-info text-white">
                                <h5 className="mb-0">
                                    <i className="fas fa-chart-pie me-2"></i>
                                    Summary Statistics
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    {userAnalyticsReport.data.map((category, index) => (
                                        <Col key={index} md={4} className="mb-3">
                                            <div 
                                                className="d-flex align-items-center p-3 border rounded"
                                                style={{ 
                                                    cursor: 'pointer',
                                                    backgroundColor: expandedCategory === index ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                                                    borderColor: expandedCategory === index ? '#3b82f6' : '#dee2e6',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onClick={() => toggleCategory(index)}
                                                onMouseEnter={(e) => {
                                                    if (expandedCategory !== index) {
                                                        e.currentTarget.style.backgroundColor = 'rgba(148, 163, 184, 0.1)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (expandedCategory !== index) {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                    }
                                                }}
                                            >
                                                {getCategoryIcon(category.category)}
                                                <div className="ms-3 flex-grow-1">
                                                    <div className="fw-bold">{category.category}</div>
                                                    <Badge bg={getCategoryVariant(category.category)} className="fs-6 mt-1">
                                                        {category.count || 0} users
                                                    </Badge>
                                                </div>
                                                <i className={`fas fa-chevron-${expandedCategory === index ? 'up' : 'down'} ms-2 text-muted`}></i>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Expanded Category Table */}
                        {expandedCategory !== null && userAnalyticsReport.data[expandedCategory] && (
                            <Card className="mt-4 shadow-sm">
                                <Card.Header className={`bg-${getCategoryVariant(userAnalyticsReport.data[expandedCategory].category)} text-white`}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            {getCategoryIcon(userAnalyticsReport.data[expandedCategory].category)}
                                            <h5 className="mb-0 ms-3">
                                                {userAnalyticsReport.data[expandedCategory].category}
                                            </h5>
                                        </div>
                                        <Button
                                            variant="light"
                                            size="sm"
                                            onClick={() => setExpandedCategory(null)}
                                        >
                                            <i className="fas fa-times"></i>
                                        </Button>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    {userAnalyticsReport.data[expandedCategory].users && userAnalyticsReport.data[expandedCategory].users.length > 0 ? (
                                        <div className="table-responsive" style={{maxHeight: '500px', overflowY: 'auto'}}>
                                            <Table striped hover responsive className="mb-0">
                                                <thead className="table-light sticky-top">
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Phone</th>
                                                        <th>Email</th>
                                                        <th>Registered Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {userAnalyticsReport.data[expandedCategory].users.map((user, userIndex) => (
                                                        <tr key={userIndex}>
                                                            <td className="fw-bold">{user.name || 'N/A'}</td>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <span>{user.phone || 'N/A'}</span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                {user.email && user.email !== 'N/A' ? (
                                                                    <div className="d-flex align-items-center">
                                                                        <Mail size={14} className="me-1 text-muted" />
                                                                        {user.email}
                                                                    </div>
                                                                ) : (
                                                                    'N/A'
                                                                )}
                                                            </td>
                                                            <td>
                                                                {user.registeredDate && user.registeredDate !== 'N/A' ? (
                                                                    <div className="d-flex align-items-center">
                                                                        <Calendar size={14} className="me-1 text-muted" />
                                                                        {formatDate(user.registeredDate)}
                                                                    </div>
                                                                ) : (
                                                                    'N/A'
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-5">
                                            <p className="text-muted mb-0">No users in this category</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        )}
                    </>
                )}

                {/* No Data State */}
                {!reportLoading && !userAnalyticsReport && (
                    <Card className="shadow-sm">
                        <Card.Body className="text-center py-5">
                            <BarChart3 size={48} className="text-muted mb-3" />
                            <p className="text-muted">No data available. Click refresh to load the report.</p>
                        </Card.Body>
                    </Card>
                )}
            </div>
        </Fragment>
    );
};

UserAnalyticsReport.propTypes = {
    getUserAnalyticsReport: PropTypes.func.isRequired,
    userAnalyticsReport: PropTypes.object,
    reportLoading: PropTypes.bool.isRequired,
    reportError: PropTypes.string
};

const mapStateToProps = state => ({
    userAnalyticsReport: state.dashboard.userAnalyticsReport,
    reportLoading: state.dashboard.reportLoading,
    reportError: state.dashboard.reportError
});

export default connect(mapStateToProps, { getUserAnalyticsReport })(UserAnalyticsReport);

